import { Outlet, useLocation } from 'react-router-dom'
import {ContentArea} from '@/components/shared/ContentArea'
import {InlineRow} from '@/components/shared/InlineRow'
import { Database } from 'lucide-react'

import { usePageTitle } from '@/hooks/use-page-title'
import { useI18n } from '@/i18n'
import { SidebarNav, type SidebarNavItem } from '@/components/layout/SidebarNav'
import { SidebarNavId } from '@/testids/SidebarNavTestIds'
import { routes } from '@/lib/routes'
import { permissions } from '@/lib/permissions'
import { CONCEPT_ICON } from '@/lib/concepts'
import { useAuth } from '@/contexts/auth-context'

import {EmptyState} from '@/components/shared/EmptyState'
import {SectionContainer} from '@/components/shared/SectionContainer'

interface SchemaMenuItem extends SidebarNavItem {
  permission: string
}

export function Schema() {
  const i18n = useI18n()
  usePageTitle(i18n.schema.pageTitle)
  const location = useLocation()
  const { hasPermission } = useAuth()
  const isRootSchema = location.pathname === routes.schema.root()

  const menuItems: SchemaMenuItem[] = [
    {
      id: SidebarNavId.Types,
      href: routes.schema.types.list(),
      icon: CONCEPT_ICON.typeValue,
      permission: permissions.types.view,
      title: i18n.schema.typeValues,
      description: i18n.schema.typeValuesDescription,
    },
    {
      id: SidebarNavId.AttributeDefinitions,
      href: routes.schema.attributeDefinitions.path(),
      icon: CONCEPT_ICON.attributeDefinition,
      permission: permissions.attributeDefinitions.view,
      title: i18n.schema.attributeDefinitions,
      description: i18n.schema.attributeDefinitionsDescription,
    },
    {
      id: SidebarNavId.FormLayouts,
      href: routes.schema.formLayouts.list(),
      icon: CONCEPT_ICON.formLayout,
      permission: permissions.formLayouts.manage,
      title: i18n.schema.formLayouts,
      description: i18n.schema.formLayoutsDescription,
    },
    {
      id: SidebarNavId.RelationshipSchema,
      href: routes.schema.relationshipSchema.path(),
      icon: CONCEPT_ICON.relationshipSchema,
      permission: permissions.partyRelationshipSchema.view,
      title: i18n.schema.relationshipSchema,
      description: i18n.schema.relationshipSchemaDescription,
    },
    {
      id: SidebarNavId.SchemaPolicies,
      href: routes.schema.policies.list(),
      icon: CONCEPT_ICON.constraint,
      permission: permissions.constraints.view,
      title: i18n.schema.schemaPolicies,
      description: i18n.schema.schemaPoliciesDescription,
    },
    {
      id: SidebarNavId.SchemaViolations,
      href: routes.schema.violations.list(),
      icon: CONCEPT_ICON.violation,
      permission: permissions.violations.view,
      title: i18n.schema.schemaViolations,
      description: i18n.schema.schemaViolationsDescription,
    },
    {
      id: SidebarNavId.ValueLists,
      href: routes.schema.valueLists.list(),
      icon: CONCEPT_ICON.valueList,
      permission: permissions.valueLists.view,
      title: i18n.schema.valueLists,
      description: i18n.schema.valueListsDescription,
    },
  ]

  const visibleMenuItems = menuItems.filter(item =>
    hasPermission(item.permission)
  )

  return (
    <SectionContainer>
      <InlineRow align="start">
        <SidebarNav items={visibleMenuItems} aria-label={String(i18n.schema.pageTitle)}/>

        {/* Content Area */}
        <ContentArea>
          {isRootSchema ? (
            <EmptyState
              variant="card"
              icon={Database}
              title={String(i18n.schema.welcome)}
              description={String(i18n.schema.selectOption)}
              data-testid="schema-welcome-card"
            />
          ) : (
            <Outlet />
          )}
        </ContentArea>
      </InlineRow>
    </SectionContainer>
  )
}
